import { useLazyQuery, ApolloError, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  CREATE_REQUEST_GROUP_FOR_PATIENT_GROUP,
  GET_REQUEST_GROUP_STATUS,
} from 'pagesAdmin/SettingsPage/gqlSchemes/createRequestGroupForPatientGroup';
import getErrorMessage from 'lib/enums/errorCodes';

interface AddCarePlanStatusResult {
  patientGroup: {
    createPatientGroupRequestGroupsStatus: {
      taskStatus: string;
      results: any;
    };
  };
}

export default function useAddCarePlanToPatientGroup(
  close: (values: any) => void
) {
  const [loadCarePlanStatus] = useLazyQuery<
    AddCarePlanStatusResult,
    { taskId: string }
  >(GET_REQUEST_GROUP_STATUS);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [createRequestGroup] = useMutation<any, { record: { id: string } }>(
    CREATE_REQUEST_GROUP_FOR_PATIENT_GROUP,
    {
      onError: (error: ApolloError) => {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      },
    }
  );

  const onAddCarePlan = async (variables: any): Promise<void> => {
    setLoading(true);
    try {
      const addResult = await createRequestGroup(variables);
      const addResultData =
        addResult?.data?.patientGroup?.createPatientGroupRequestGroups;

      if (!addResultData?.ok) {
        setLoading(false);
        enqueueSnackbar(
          t(
            getErrorMessage({
              code: addResultData?.error?.code,
              params: addResultData?.error?.params,
            })
          ),
          {
            variant: 'error',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          }
        );
      }

      const taskId = addResultData.result?.taskId;
      enqueueSnackbar(t('This may take some time, please wait'), {
        variant: 'info',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
      const checkStatus = async () => {
        const { data } = await loadCarePlanStatus({ variables: { taskId } });
        const status =
          data?.patientGroup?.createPatientGroupRequestGroupsStatus?.taskStatus;
        const results =
          data?.patientGroup?.createPatientGroupRequestGroupsStatus?.results;

        if (status === 'SUCCESS') {
          setLoading(false);

          const errors = results?.filter((result: any) => !result.ok);
          if (errors?.length) {
            enqueueSnackbar(
              t('Care plan was not assigned to {{errorsNumber}} patients.', {
                errorsNumber: errors.length,
              }),
              {
                variant: 'warning',
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'center',
                },
              }
            );
          }
          enqueueSnackbar(t('Care plan added successfully'), {
            variant: 'success',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          });

          close(data);
          return true;
        }
        return false;
      };

      const interval = setInterval(async () => {
        if (await checkStatus()) {
          clearInterval(interval);
        }
      }, 3000);

      setTimeout(() => {
        clearInterval(interval);
        setLoading(false);
        enqueueSnackbar(t('Something went wrong. Please, try again later'), {
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        });
      }, 60000);
    } catch (error) {
      setLoading(false);
      console.error('Error:', error);
    }
  };

  return {
    onAddCarePlan,
    loading,
  };
}
